import React, { useContext, useState, useEffect } from 'react';
import { Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import SessionContext from '@providers/Session/SessionContext';
import useSwr from '@hooks/useSwr';

interface TransactionByTypeFieldProps {
  options: Array<{ label: string; value: string }>;
  loading: boolean;
  disabled?: boolean;
}

export function TransactionByTypeField({
  options,
  loading,
  disabled = false,
}: TransactionByTypeFieldProps) {
  const { t } = useTranslation('create-alarm-step');
  const { customer } = useContext(SessionContext);
  const customerId = customer?.customerLogged?.id;
  const [selectedAppName, setSelectedAppName] = useState<string | undefined>();
  const form = Form.useFormInstance();

  const { data: statusData } = useSwr<{
    data?: string[];
  }>(
    customerId && selectedAppName
      ? `/service-proxy/dashboard/transacao/alarme/kapacitor/alerta_task/listar_transacao_status?appName=${selectedAppName}&customerId=${customerId}`
      : null,
    {
      headers: {
        customerId,
        appName: selectedAppName,
      },
    },
  );

  useEffect(() => {
    if (selectedAppName) {
      form.setFieldValue('transactionByTypeStatus', undefined);
    }
  }, [selectedAppName, form]);

  useEffect(() => {
    const formValue = form.getFieldValue('transactionByType');
    if (formValue && !selectedAppName) {
      setSelectedAppName(formValue);
    }
  }, [form, selectedAppName]);

  // Classificação binária: SUCCESS = Sucesso, resto = Falha
  const statusOptions = React.useMemo(() => {
    const rawStatuses = Array.isArray(statusData)
      ? statusData
      : statusData?.data || [];

    const hasSuccess = rawStatuses.includes('SUCCESS');
    const hasFailures = rawStatuses.some(status => status !== 'SUCCESS');

    const statusOpts = [];
    if (hasSuccess) {
      statusOpts.push({
        label: t(
          'create-alarm-step:stepOne.createAlarmTransactionsByTypeFieldSuccess',
        ),
        value: 'SUCCESS',
      });
    }
    if (hasFailures) {
      statusOpts.push({
        label: t(
          'create-alarm-step:stepOne.createAlarmTransactionsByTypeFieldFailure',
        ),
        value: 'FAILURE',
      });
    }

    return statusOpts;
  }, [statusData, t]);

  return (
    <>
      <Form.Item
        name="transactionByType"
        label={t(
          'create-alarm-step:stepOne.createAlarmTransactionsByTypeField',
        )}
        rules={[
          {
            required: true,
            message: t(
              'create-alarm-step:stepOne.createAlarmTransactionsByTypeRequired',
            ),
          },
        ]}
      >
        <Select
          allowClear
          options={options}
          loading={loading}
          disabled={loading || disabled}
          style={{ textAlign: 'left' }}
          onChange={value => {
            setSelectedAppName(value);
            form.setFieldValue('transactionByTypeStatus', undefined);
          }}
        />
      </Form.Item>

      {selectedAppName && (
        <Form.Item
          name="transactionByTypeStatus"
          label={t('create-alarm-step:stepOne.createAlarmStatusField')}
          rules={[
            {
              required: true,
              message: t(
                'create-alarm-step:stepOne.createAlarmStatusFieldRequired',
              ),
            },
          ]}
        >
          <Select
            allowClear
            options={statusOptions}
            loading={!statusData}
            disabled={!statusData}
            style={{ textAlign: 'left' }}
          />
        </Form.Item>
      )}
    </>
  );
}
